import { useDebounce } from "@/hooks/useDebounce";
import type { UseInvoicesPageReturn } from "@/types/invoices";
import api from "@shared/api/client";
import type { Address, Invoice, Provider, Status } from "@shared/types/models";
import type { PaginatedInvoiceListParams, SortConfig } from "@shared/types/payment";
import { SortColumn, SortDirection } from "@shared/types/payment";
import { useCallback, useEffect, useRef, useState } from "react";

const useInvoicesPage = (): UseInvoicesPageReturn => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filterProviderIds, setFilterProviderIds] = useState<number[]>([]);
  const [filterAddressIds, setFilterAddressIds] = useState<number[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState<SortConfig>({ column: SortColumn.CREATED_AT, direction: SortDirection.DESC });
  const [providers, setProviders] = useState<Provider[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const debouncedStatuses = useDebounce(statuses, 300);
  const invoicesRef = useRef<Invoice[]>([]);

  useEffect(() => {
    invoicesRef.current = invoices;
  }, [invoices]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginatedInvoiceListParams = { page, limit };
      if (debouncedStatuses?.length) params.status = debouncedStatuses;
      if (filterProviderIds?.length) params.providerId = filterProviderIds;
      if (filterAddressIds?.length) params.addressId = filterAddressIds;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (sort.column) {
        params.sortBy = sort.column;
        params.sortOrder = sort.direction ?? SortDirection.DESC;
      }
      const res = await api.invoices.list(params);
      setInvoices(res.data);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedStatuses, page, limit, filterProviderIds, filterAddressIds, dateFrom, dateTo, sort]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    Promise.all([api.providers.list(), api.addresses.list()])
      .then(([providersRes, addressesRes]) => {
        if (mountedRef.current) {
          setProviders(providersRes);
          setAddresses(
            addressesRes.map((addr) => ({
              ...addr,
              providers: addr.providers ?? [],
            })),
          );
        }
      })
      .catch((error) => {
        console.error("Failed to load providers/addresses:", error);
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSort = useCallback((column: SortColumn) => {
    setSort((prev) => {
      if (prev.column === column) {
        if (prev.direction === SortDirection.DESC) return { column, direction: SortDirection.ASC };
        return { column: undefined, direction: undefined };
      }
      return { column, direction: SortDirection.DESC };
    });
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((n: number) => {
    setLimit(n);
    setPage(1);
  }, []);

  const handleDeleteInvoice = useCallback(
    async (invoiceId: number) => {
      await api.invoices.remove(invoiceId);

      const remaining = invoicesRef.current.filter((inv) => inv.id !== invoiceId);
      const shouldGoBackAPage = page > 1 && remaining.length < limit;

      setInvoices(remaining);
      setTotal((prev) => Math.max(0, prev - 1));
      if (shouldGoBackAPage) setPage((p) => Math.max(1, p - 1));
    },
    [page, limit],
  );

  return {
    invoices,
    total,
    totalPages,
    loading,
    statuses,
    setStatuses,
    page,
    setPage,
    limit,
    setLimit,
    filterProviderIds,
    setFilterProviderIds,
    filterAddressIds,
    setFilterAddressIds,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sort,
    setSort,
    providers,
    addresses,
    fetchInvoices,
    handleSort,
    handlePageSizeChange,
    handleDeleteInvoice,
  };
};

export { useInvoicesPage };
